/**
 * ContentPostGridService (Phase 3.1) — atomic grid-side operations on
 * `projects/{pid}/content_posts/*`. Pairs the per-cell PostsGrid UI with
 * the data ops it triggers outside the AI generation path:
 *
 *   - promotePrimary       — flip isPrimaryVariant within a cell (atomic
 *                            transaction so old primary loses the flag the
 *                            same instant the new one gains it). Also
 *                            updates any ContentPublication that pointed at
 *                            the old primary to point at the new one.
 *   - archiveMaster        — set lifecycleStage='archived' on a master doc.
 *                            Used by master-regen flow before kicking the
 *                            new master generation request.
 *   - markDerivativesStale — stamp `staleSince: Timestamp.now()` on every
 *                            derivative whose `sourcePostId === oldMasterId`.
 *                            Surface: derivative cells render a `(stale)`
 *                            badge + row gets a "Regenerate stale" chip.
 *   - deletePost           — remove a single ContentPost doc (used by
 *                            cell trash buttons in Phase 3+).
 *
 * All operations are explicit batch / transaction so the grid renders
 * the new state on the next snapshot without any "intermediate" frames.
 */

import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp,
  writeBatch,
  runTransaction,
  deleteDoc,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { PlatformId } from '../types';

class ContentPostGridService {
  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private collRef(projectId: string) {
    return collection(this.db, 'projects', projectId, 'content_posts');
  }

  /**
   * Atomically promote a variant to primary within `(topicId, aspectKey, platform)`.
   *
   * Steps inside one Firestore transaction:
   *  1. Find the current primary (where isPrimaryVariant=true). Set it false.
   *  2. Set the target's isPrimaryVariant=true.
   *
   * If a `ContentPublication` referenced the old primary by `contentPostId`,
   * we update it in the same transaction to point at the new primary so the
   * Calendar tab re-renders immediately on next snapshot.
   */
  async promotePrimary(
    projectId: string,
    opts: {
      topicId: string;
      aspectKey: string | null;
      platform: PlatformId;
      newPrimaryId: string;
    },
  ): Promise<void> {
    const { topicId, aspectKey, platform, newPrimaryId } = opts;
    // Pre-query: find existing primaries in the same cell. Read OUTSIDE the
    // transaction (Firestore Web SDK doesn't allow get-by-query inside it),
    // then transactionally write the toggles.
    const snap = await getDocs(query(
      this.collRef(projectId),
      where('topicId', '==', topicId),
      where('aspectKey', '==', aspectKey),
      where('isPrimaryVariant', '==', true),
    ));
    const oldPrimaries = snap.docs.filter((d) => {
      const data = d.data() as { platforms?: PlatformId[] | null; id?: string };
      return (data.platforms ?? []).includes(platform) && d.id !== newPrimaryId;
    });

    await runTransaction(this.db, async (tx) => {
      for (const d of oldPrimaries) {
        tx.update(d.ref, {
          isPrimaryVariant: false,
          updatedAt: serverTimestamp(),
        });
      }
      tx.update(doc(this.collRef(projectId), newPrimaryId), {
        isPrimaryVariant: true,
        updatedAt: serverTimestamp(),
      });
    });

    // ContentPublication redirect — Calendar slots that referenced the old
    // primary should follow the promotion. Done outside the transaction so
    // a missing index / collection doesn't fail the core flip; we just log.
    try {
      for (const d of oldPrimaries) {
        const pubsSnap = await getDocs(query(
          collectionGroup(this.db, 'publications'),
          where('projectId', '==', projectId),
          where('contentPostId', '==', d.id),
        ));
        const batch = writeBatch(this.db);
        pubsSnap.docs.forEach((p) => batch.update(p.ref, {
          contentPostId: newPrimaryId,
          updatedAt: serverTimestamp(),
        }));
        if (!pubsSnap.empty) await batch.commit();
      }
    } catch (e) {
      // Non-fatal — Calendar tab will still resolve via cell re-render.
      console.warn('[ContentPostGridService] publication redirect failed:', (e as Error).message);
    }
  }

  /**
   * Archive a master post (used in master-regen flow before re-generation).
   * `lifecycleStage='archived'` is filtered out by the grid's
   * `useTopicPostsGrid` hook so the cell visually clears immediately.
   */
  async archiveMaster(projectId: string, masterId: string): Promise<void> {
    await runTransaction(this.db, async (tx) => {
      tx.update(doc(this.collRef(projectId), masterId), {
        lifecycleStage: 'archived',
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
  }

  /**
   * Stamp `staleSince` on every derivative whose `sourcePostId === oldMasterId`.
   * Triggered right after `archiveMaster` so the row visually flips to the
   * stale state in one snapshot frame.
   *
   * Returns the count of derivatives marked, so the UI can surface
   * "Marked N derivatives as stale" feedback.
   */
  async markDerivativesStale(
    projectId: string,
    oldMasterId: string,
  ): Promise<number> {
    const snap = await getDocs(query(
      this.collRef(projectId),
      where('sourcePostId', '==', oldMasterId),
    ));
    if (snap.empty) return 0;
    const batch = writeBatch(this.db);
    const now = serverTimestamp();
    snap.docs.forEach((d) => batch.update(d.ref, {
      staleSince: now,
      updatedAt: now,
    }));
    await batch.commit();
    return snap.size;
  }

  /**
   * Convenience: archive master + mark derivatives stale in sequence.
   * Used by `<MasterCell>`'s Regen flow before triggering the new master
   * generation request.
   */
  async prepareMasterRegen(
    projectId: string,
    oldMasterId: string,
  ): Promise<{ markedStaleCount: number }> {
    await this.archiveMaster(projectId, oldMasterId);
    const markedStaleCount = await this.markDerivativesStale(projectId, oldMasterId);
    return { markedStaleCount };
  }

  async deletePost(projectId: string, postId: string): Promise<void> {
    await deleteDoc(doc(this.collRef(projectId), postId));
  }
}

export const contentPostGridService = new ContentPostGridService();
export { ContentPostGridService };
